// Minha Casa (0.34): tarefas da casa simples, só nesta aba. O app calcula o atraso pela frequência:
// começa no dia em que a tarefa foi criada e anota sozinho a última vez quando a pessoa marca como feita.
import { getDoc, setDoc } from './store';
import type { Docs, Room, TarefaCasa } from './types';
import { occursOn } from '../lib/recur';
import { addDays, parseYmd, today, uid } from '../lib/date';
import { deleteTask } from './planner';
import { novoBloco } from './anotacoes';

type D = Docs['casa'];
const mut = (fn: (d: D) => D) => setDoc('casa', fn);
export const tarefasCasa = (d: D = getDoc('casa')) => d.tarefas ?? [];

export function novaTarefaCasa(comodo: string | null = null, nome = '', emoji = '🧹'): TarefaCasa {
  return { id: uid(), nome, emoji, comodo, recur: { type: 'weekly' }, subs: [], obs: [novoBloco('check')], criado: today(), ultima: null };
}
export function salvarTarefaCasa(x: TarefaCasa) {
  mut(d => { const l = tarefasCasa(d); return { ...d, tarefas: l.some(y => y.id === x.id) ? l.map(y => (y.id === x.id ? x : y)) : [...l, x] }; });
}
export const excluirTarefaCasa = (id: string) => mut(d => ({ ...d, tarefas: tarefasCasa(d).filter(x => x.id !== id) }));
const mudar = (id: string, fn: (x: TarefaCasa) => TarefaCasa) => mut(d => ({ ...d, tarefas: tarefasCasa(d).map(x => (x.id === id ? fn(x) : x)) }));

/** Marca como feita hoje (a próxima vez sai da frequência). Feita de novo no mesmo dia desfaz. */
export function marcarFeita(id: string) {
  mudar(id, x => (x.ultima === today()
    ? { ...x, ultima: x.antes ?? null, antes: undefined } as TarefaCasa
    : { ...x, antes: x.ultima, ultima: today(), subs: x.subs.map(s => ({ ...s, done: false })) } as TarefaCasa));
}
export const marcarSub = (id: string, sid: string) => mudar(id, x => ({ ...x, subs: x.subs.map(s => (s.id === sid ? { ...s, done: !s.done } : s)) }));

/** Próxima data devida: a primeira ocorrência depois da última vez (ou desde que foi criada). */
export function proximaVez(x: TarefaCasa): string | null {
  const base = { start: x.criado, end: null, recur: x.recur, skip: [] as string[] };
  if (x.recur.type === 'none') return x.ultima ? null : x.criado;
  let d = x.ultima ? addDays(x.ultima, 1) : x.criado;
  for (let i = 0; i < 800; i++, d = addDays(d, 1)) if (occursOn(base, d)) return d;
  return null;
}
const dias = (de: string, ate: string) => Math.round((parseYmd(ate).getTime() - parseYmd(de).getTime()) / 864e5);

export type Grupo = 'atrasadas' | 'vencer' | 'adiante' | 'feitas';
export function situacaoCasa(x: TarefaCasa, hoje = today()): { grupo: Grupo; prox: string | null; dias: number } {
  const prox = proximaVez(x);
  if (!prox) return { grupo: 'feitas', prox: null, dias: 0 };
  const n = dias(hoje, prox);
  return { grupo: n < 0 ? 'atrasadas' : n <= 7 ? 'vencer' : 'adiante', prox, dias: n };
}

/** Lista por atraso, em grupos. Dentro do grupo vale a ordem que a pessoa arrastou; o resto segue o atraso. */
export function gruposCasa(l: TarefaCasa[]) {
  const g: Record<Grupo, { x: TarefaCasa; s: ReturnType<typeof situacaoCasa> }[]> = { atrasadas: [], vencer: [], adiante: [], feitas: [] };
  for (const x of l) { const s = situacaoCasa(x); g[s.grupo].push({ x, s }); }
  for (const k of Object.keys(g) as Grupo[]) g[k].sort((a, b) => (a.x.ordem ?? 1e9) - (b.x.ordem ?? 1e9) || (a.s.prox ?? '').localeCompare(b.s.prox ?? ''));
  return g;
}
export function ordenarGrupo(ids: string[]) {
  const pos = new Map(ids.map((id, i) => [id, i]));
  mut(d => ({ ...d, tarefas: tarefasCasa(d).map(x => (pos.has(x.id) ? { ...x, ordem: pos.get(x.id) } : x)) }));
}
export function ordenarNoComodo(ids: string[]) {
  const pos = new Map(ids.map((id, i) => [id, i]));
  mut(d => ({ ...d, tarefas: tarefasCasa(d).map(x => (pos.has(x.id) ? { ...x, ordemComodo: pos.get(x.id) } : x)) }));
}
export const doComodo = (l: TarefaCasa[], comodo: string) => l.filter(x => x.comodo === comodo).sort((a, b) => (a.ordemComodo ?? 1e9) - (b.ordemComodo ?? 1e9));
export const moverParaComodo = (id: string, comodo: string) => mudar(id, x => ({ ...x, comodo, ordemComodo: undefined }));
export const ordenarComodos = (rooms: Room[]) => mut(d => ({ ...d, rooms }));
export const ligarPlanner = (id: string, task: string) => mudar(id, x => ({ ...x, planner: task }));

/** Uma vez só: as tarefas da casa que estavam no My Planner passam a ser tarefas simples de Minha Casa (mesmo id, não duplica). */
export function migrarTarefasDoPlanner() {
  if (getDoc('casa').migrou034) return 0;
  const rooms = new Set(getDoc('casa').rooms.map(r => r.id));
  const velhas = getDoc('planner').tasks.filter(x => x.area === 'casa' || x.link?.aba === 'casa');
  const novas: TarefaCasa[] = velhas.map(x => {
    const obs = x.notes.trim() ? [{ ...novoBloco('text'), text: x.notes.trim() }] : [novoBloco('check')];
    return { id: x.id, nome: x.title, emoji: x.emoji, comodo: x.link?.aba === 'casa' && x.link.id && rooms.has(x.link.id) ? x.link.id : null,
      recur: x.recur.type === 'none' && x.end ? { type: 'daily' } : x.recur, subs: x.subtasks.map(s => ({ id: s.id, text: s.text, done: false })),
      obs, criado: x.start, ultima: [...x.done].sort().pop() ?? null };
  });
  mut(d => {
    const ja = new Set(tarefasCasa(d).map(x => x.id));
    return { ...d, migrou034: true, tarefas: [...tarefasCasa(d), ...novas.filter(x => !ja.has(x.id))] };
  });
  for (const x of velhas) deleteTask(x.id, 'all', x.start);
  return novas.length;
}
